import { Divider, Stack, Typography } from "@mui/material";
import { differenceInHours, parseISO } from "date-fns";
import { useEffect, useState, useMemo } from "react";
import CardSlider from "@/components/common/cardSlider/cardSlider";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";
import FeaturedDealsPromoCard from "./featuredDealsPromoCard";
import OldPackageCard from "./oldPackageCard/oldPackageCard";
import PackageCard from "./packageCard/packageCard";

type Props = {
  packages: MedspaPackage[];
  customizationPrimaryColor: string;
  medspaId: string;
  customizationEnabled: boolean;
  setIsCheckoutDrawerOpen: (isOpen: boolean) => void;
  packageCardRevampV1Enabled: boolean;
};

export default function FeaturedDealsSection({
  packages,
  customizationPrimaryColor,
  medspaId,
  customizationEnabled,
  setIsCheckoutDrawerOpen,
  packageCardRevampV1Enabled,
}: Props) {
  const [isClient, setIsClient] = useState(false);
  const isMobile = useMoxieMediaQuery("ipad-hor", "down");

  // This is needed to prevent hydration error
  useEffect(() => {
    setIsClient(true);
  }, []);

  const { firstPackageToExpire, packagesCards } = useMemo(
    () =>
      preparePackages(
        packages,
        customizationEnabled,
        customizationPrimaryColor,
        medspaId,
        setIsCheckoutDrawerOpen,
        packageCardRevampV1Enabled
      ),
    [
      packages,
      customizationEnabled,
      customizationPrimaryColor,
      medspaId,
      setIsCheckoutDrawerOpen,
      packageCardRevampV1Enabled,
    ]
  );

  const cards = useMemo(
    () => [
      ...(firstPackageToExpire.endDate
        ? [
            {
              id: "featured-deals-promo",
              content: (
                <FeaturedDealsPromoCard
                  endDate={firstPackageToExpire.endDate}
                  primaryColor={customizationPrimaryColor}
                />
              ),
            },
          ]
        : []),
      ...packagesCards,
    ],
    [firstPackageToExpire.endDate, customizationPrimaryColor, packagesCards]
  );

  return (
    <>
      <Stack
        sx={{
          mt: 3,
          gap: 1,
        }}
      >
        <Typography variant="h1">Featured deals</Typography>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          Explore our exclusive deals and save big on high-quality items.
        </Typography>
        {isMobile &&
          packageCardRevampV1Enabled &&
          firstPackageToExpire.endDate && (
            <FeaturedDealsPromoCard
              endDate={firstPackageToExpire.endDate}
              primaryColor={customizationPrimaryColor}
            />
          )}
      </Stack>
      {isClient && (
        <CardSlider
          cards={isMobile && packageCardRevampV1Enabled ? packagesCards : cards}
          primaryColor={customizationPrimaryColor}
          slidesPerBreakpoint={{
            xs: packageCardRevampV1Enabled ? 2 : 1,
          }}
        />
      )}
      <Divider sx={{ my: 5 }} />
    </>
  );
}

const preparePackages = (
  packages: MedspaPackage[],
  customizationEnabled: boolean,
  customizationPrimaryColor: string,
  medspaId: string,
  setIsCheckoutDrawerOpen: (isOpen: boolean) => void,
  packageCardRevampV1Enabled: boolean
) => {
  return packages.reduce(
    ({ firstPackageToExpire, packagesCards }, packageItem) => {
      const difference = differenceInHours(
        parseISO(packageItem.endDate),
        new Date()
      );

      if (difference < firstPackageToExpire.hours) {
        firstPackageToExpire = {
          endDate: packageItem.endDate,
          hours: difference,
        };
      }

      packagesCards.push({
        id: `featured-deal-${packageItem.id}`,
        content: packageCardRevampV1Enabled ? (
          <PackageCard
            packageItem={packageItem}
            isFeaturedDeal
            customizationEnabled={customizationEnabled}
            customizationPrimaryColor={customizationPrimaryColor}
            medspaId={medspaId}
            setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
          />
        ) : (
          <OldPackageCard
            packageItem={packageItem}
            customizationEnabled={customizationEnabled}
            customizationPrimaryColor={customizationPrimaryColor}
            medspaId={medspaId}
          />
        ),
      });

      return { firstPackageToExpire, packagesCards };
    },
    {
      firstPackageToExpire: { endDate: null, hours: Infinity },
      packagesCards: [],
    }
  );
};
